import type { AvailabilityBlock } from '@/app/dashboard/settings/_ui/availabilityStorage';
import { hhmmToMinutes, minutesToHHmm } from '@/components/availability/timeUtils';

/**
 * The weekly-hours editor's block operations, pure: the Settings store and the
 * claim card both edit the same seven rows, and one copy of "what a new block
 * defaults to" keeps the two from drifting.
 */
export interface BlockSet {
  blocks: AvailabilityBlock[];
  disabledDays: Set<number>;
}

const DAY_DEFAULT = { start: '09:00', end: '17:00' };
const NEW_BLOCK_MINUTES = 120;

function newBlockId(prefix: string): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

const WEEKDAYS = [0, 1, 2, 3, 4];
const WEEKEND = [5, 6];

/** A week open on `days` from `start` to `end`, every other day off. */
function openOn(days: number[], start: string, end: string): BlockSet {
  return {
    blocks: days.map((day) => ({ id: `claim_${day}`, day, start, end })),
    disabledDays: new Set([0, 1, 2, 3, 4, 5, 6].filter((d) => !days.includes(d))),
  };
}

/** The weeks a new page offers to open with; the first is the one it opens on. */
export const CLAIM_HOUR_PRESETS = [
  { label: 'Weekday evenings', week: () => openOn(WEEKDAYS, '18:00', '21:00') },
  { label: 'Weekday daytime', week: () => openOn(WEEKDAYS, '10:00', '17:00') },
  { label: 'Weekends', week: () => openOn(WEEKEND, '10:00', '16:00') },
] as const;

export function defaultClaimHours(): BlockSet {
  return CLAIM_HOUR_PRESETS[0].week();
}

export function blocksByDay(blocks: AvailabilityBlock[]): Map<number, AvailabilityBlock[]> {
  const map = new Map<number, AvailabilityBlock[]>();
  for (const b of blocks) {
    const arr = map.get(b.day) ?? [];
    arr.push(b);
    map.set(b.day, arr);
  }
  for (const arr of map.values()) arr.sort((a, b) => a.start.localeCompare(b.start));
  return map;
}

const SHORT_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The week as a reader says it: consecutive days with the same hours fold into one line. */
export function weekLines(set: BlockSet): string[] {
  const byDay = blocksByDay(set.blocks);
  const hours = SHORT_DAYS.map((_, day) =>
    set.disabledDays.has(day)
      ? ''
      : (byDay.get(day) ?? []).map((b) => `${b.start}–${b.end}`).join(', ')
  );
  const lines: string[] = [];
  for (let day = 0; day < 7; day++) {
    if (!hours[day]) continue;
    let last = day;
    while (last + 1 < 7 && hours[last + 1] === hours[day]) last++;
    const days = last === day ? SHORT_DAYS[day] : `${SHORT_DAYS[day]}–${SHORT_DAYS[last]}`;
    lines.push(`${days} ${hours[day]}`);
    day = last;
  }
  return lines;
}

export function validateBlocks(blocks: AvailabilityBlock[]): Map<string, string> {
  const errors = new Map<string, string>();
  const byDay = new Map<number, AvailabilityBlock[]>();

  for (const b of blocks) {
    if (!b.start || !b.end) {
      errors.set(b.id, 'Set a start and an end time.');
      continue;
    }
    if (b.start >= b.end) {
      errors.set(b.id, 'End time must be after start time.');
      continue;
    }
    const dayBlocks = byDay.get(b.day);
    if (dayBlocks) dayBlocks.push(b);
    else byDay.set(b.day, [b]);
  }

  for (const dayBlocks of byDay.values()) {
    if (dayBlocks.length < 2) continue;
    dayBlocks.sort((a, b) => a.start.localeCompare(b.start));
    for (let i = 1; i < dayBlocks.length; i++) {
      const prev = dayBlocks[i - 1];
      const curr = dayBlocks[i];
      if (curr.start < prev.end) {
        errors.set(prev.id, `Overlaps with ${curr.start}-${curr.end}`);
        errors.set(curr.id, `Overlaps with ${prev.start}-${prev.end}`);
      }
    }
  }
  return errors;
}

/** Turning a day on that has no rows gives it one working day; turning it off keeps its rows for later. */
export function setDayEnabled(
  set: BlockSet,
  day: number,
  enabled: boolean,
  idPrefix: string
): BlockSet {
  if (enabled === !set.disabledDays.has(day)) return set;
  const disabledDays = new Set(set.disabledDays);
  if (enabled) disabledDays.delete(day);
  else disabledDays.add(day);
  const blocks =
    enabled && !set.blocks.some((b) => b.day === day)
      ? [...set.blocks, { id: newBlockId(idPrefix), day, ...DAY_DEFAULT }]
      : set.blocks;
  return { blocks, disabledDays };
}

/** The new block follows the day's last one; with no room after it, it goes before the first. */
export function addBlock(set: BlockSet, day: number, idPrefix: string): BlockSet {
  const dayBlocks = set.blocks.filter((b) => b.day === day);
  let start = '10:00';
  let end = '12:00';

  if (dayBlocks.length > 0) {
    const lastEnd = Math.max(...dayBlocks.map((b) => hhmmToMinutes(b.end)));
    const firstStart = Math.min(...dayBlocks.map((b) => hhmmToMinutes(b.start)));
    if (lastEnd + NEW_BLOCK_MINUTES <= 24 * 60) {
      start = minutesToHHmm(lastEnd);
      end = minutesToHHmm(lastEnd + NEW_BLOCK_MINUTES);
    } else if (firstStart >= NEW_BLOCK_MINUTES) {
      start = minutesToHHmm(firstStart - NEW_BLOCK_MINUTES);
      end = minutesToHHmm(firstStart);
    } else {
      start = '00:00';
      end = minutesToHHmm(NEW_BLOCK_MINUTES);
    }
  }

  return { ...set, blocks: [...set.blocks, { id: newBlockId(idPrefix), day, start, end }] };
}

/** Removing a day's only block turns the day off instead, so the row keeps a shape to come back to. */
export function removeBlock(set: BlockSet, blockId: string): BlockSet {
  const block = set.blocks.find((b) => b.id === blockId);
  if (!block) return set;
  const siblings = set.blocks.filter((b) => b.day === block.day);
  if (siblings.length <= 1) {
    const disabledDays = new Set(set.disabledDays);
    disabledDays.add(block.day);
    return { ...set, disabledDays };
  }
  return { ...set, blocks: set.blocks.filter((b) => b.id !== blockId) };
}

/** A start moved past its end pushes the end an hour on, capped at midnight. */
export function updateBlock(
  set: BlockSet,
  blockId: string,
  patch: Partial<Pick<AvailabilityBlock, 'start' | 'end'>>
): BlockSet {
  const blocks = set.blocks.map((b) => {
    if (b.id !== blockId) return b;
    const updated = { ...b, ...patch };
    if (patch.start !== undefined && hhmmToMinutes(updated.start) >= hhmmToMinutes(updated.end)) {
      updated.end = minutesToHHmm(Math.min(hhmmToMinutes(updated.start) + 60, 1440));
    }
    return updated;
  });
  return { ...set, blocks };
}

/** Cal.com's "Copy times to": the targets' own rows are replaced by the source day's, and they open. */
export function copyDay(set: BlockSet, from: number, to: number[], idPrefix: string): BlockSet {
  const targets = new Set(to.filter((d) => d !== from));
  const source = set.blocks.filter((b) => b.day === from);
  const disabledDays = new Set(set.disabledDays);
  targets.forEach((d) => disabledDays.delete(d));
  const copies = [...targets].flatMap((day) =>
    source.map((b) => ({ id: newBlockId(idPrefix), day, start: b.start, end: b.end }))
  );
  return { blocks: [...set.blocks.filter((b) => !targets.has(b.day)), ...copies], disabledDays };
}
